import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Flex } from "../../../components/Flex";
import { Info } from "../../../components/Info";
import { Text } from "../../../components/Text";
import { WarningModal } from "../../../components/WarningModal";
import { settingsSliceSelectors } from "../../../redux/settings/settings.selector";
import { settingsActions } from "../../../redux/settings/settings.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { isImpersonation } from "../../../redux/store-utils";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Spacing } from "../../../theme/Spacing";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Switch } from "../../../utils/local-antd";
import { SettingsWrapper } from "../components/SettingsWrapper";

const Row = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  gap: ${Spacing.m};
`;

export const SettingsSupportAccess: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const settings = useAppSelector(settingsSliceSelectors.selectSettings);
  const enabled = settings?.allowSupportAccess === true;

  const [warningOpen, setWarningOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const save = async (allowSupportAccess: boolean) => {
    try {
      setLoading(true);
      await dispatch(
        settingsActions.updateSettings({ userId, allowSupportAccess }),
      ).unwrap();
      await dispatch(settingsActions.getSettings({ userId })).unwrap();
      AtiraToast.success(
        t(
          allowSupportAccess
            ? "settings.securityPassword.supportAccess.enabled"
            : "settings.securityPassword.supportAccess.disabled",
        ),
      );
      setWarningOpen(false);
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  const onToggle = (checked: boolean) => {
    if (checked) {
      setWarningOpen(true);
    } else {
      save(false);
    }
  };

  if (isImpersonation()) {
    return null;
  }

  return (
    <SettingsWrapper>
      <Text fontWeight={"bold"}>
        {t("settings.securityPassword.supportAccess.title")}
      </Text>

      <Info info={t("settings.securityPassword.supportAccess.description")} />

      <Row>
        <Flex flexDirection="column" gap="xs">
          <Text fontWeight="bold" fontSize="s">
            {t("settings.securityPassword.supportAccess.label")}
          </Text>
          <Text color="gray" fontSize="xs">
            {t("settings.securityPassword.supportAccess.hint")}
          </Text>
        </Flex>

        <Switch
          checked={enabled}
          onChange={onToggle}
          loading={loading || !settings}
        />
      </Row>

      <WarningModal
        isOpen={warningOpen}
        onClose={() => setWarningOpen(false)}
        onConfirm={() => save(true)}
        loading={loading}
        title={t("settings.securityPassword.supportAccess.warning.title")}
        description={t(
          "settings.securityPassword.supportAccess.warning.description",
        )}
        confirmButtonText={t(
          "settings.securityPassword.supportAccess.warning.confirm",
        )}
      />
    </SettingsWrapper>
  );
};
